import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import i18n from '../i18n/index';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function SettingsPage() {
    const { t } = useTranslation();
    const { user, setUser } = useAuth();

    const [language, setLanguage] = useState(user?.language || 'en');
    const [theme, setTheme] = useState(user?.theme || 'light');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const applyTheme = (selectedTheme) => {
        if (selectedTheme === 'dark') {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
        localStorage.setItem('theme', selectedTheme);
    };

    const saveSettingsPatch = async (patch, rollback) => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            await authService.updateSettings(patch);
            if (setUser) {
                setUser(prev => ({ ...prev, ...patch }));
            }
            setSuccess(t('settings.saved'));
        } catch (err) {
            rollback();
            setError(t('common.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleLanguageSelect = async (selectedLanguage) => {
        if (selectedLanguage === language || saving) return;

        const previousLanguage = language;
        setLanguage(selectedLanguage);
        i18n.changeLanguage(selectedLanguage);
        localStorage.setItem('language', selectedLanguage);

        await saveSettingsPatch(
            { language: selectedLanguage },
            () => {
                setLanguage(previousLanguage);
                i18n.changeLanguage(previousLanguage);
                localStorage.setItem('language', previousLanguage);
            }
        );
    };

    const handleThemeSelect = async (selectedTheme) => {
        if (selectedTheme === theme || saving) return;

        const previousTheme = theme;
        setTheme(selectedTheme);
        applyTheme(selectedTheme);

        await saveSettingsPatch(
            { theme: selectedTheme },
            () => {
                setTheme(previousTheme);
                applyTheme(previousTheme);
            }
        );
    };

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError(t('settings.errors.all_password_fields_required'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError(t('settings.errors.new_passwords_no_match'));
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError(t('settings.errors.password_min_length'));
            return;
        }

        setChangingPassword(true);
        try {
            await authService.updateSettings({
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });
            setPasswordSuccess(t('settings.password_changed'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPasswordError(err.response?.data?.error || t('common.error'));
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('settings.title')}</h1>
                <p className="subtitle">{t('settings.subtitle')}</p>

                {/* Language */}
                <div className="card" style={{ marginTop: '1.5rem' }}>
                    <h2>{t('settings.language')}</h2>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        {t('settings.language_subtitle')}
                    </p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {[
                            { value: 'en', label: t('settings.english'), flag: '🇬🇧' },
                            { value: 'fr', label: t('settings.french'), flag: '🇫🇷' },
                        ].map(lang => (
                            <div
                                key={lang.value}
                                onClick={() => handleLanguageSelect(lang.value)}
                                style={{
                                    flex: 1,
                                    padding: '1rem',
                                    border: `2px solid ${language === lang.value ? '#3b82f6' : '#e8ecf0'}`,
                                    borderRadius: '10px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    background: language === lang.value ? '#eff6ff' : 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    transition: 'all 0.2s',
                                    opacity: saving ? 0.7 : 1,
                                }}
                            >
                                <span style={{ fontSize: '1.5rem' }}>{lang.flag}</span>
                                <span style={{
                                    fontWeight: language === lang.value ? '700' : '500',
                                    color: language === lang.value ? '#1d4ed8' : '#1e293b',
                                }}>
                                    {lang.label}
                                </span>
                                {language === lang.value && (
                                    <span style={{
                                        marginLeft: 'auto',
                                        background: '#3b82f6',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                    }}>✓</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Theme */}
                <div className="card">
                    <h2>{t('settings.theme')}</h2>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        {t('settings.theme_subtitle')}
                    </p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {[
                            { value: 'light', label: t('settings.light'), icon: '☀️' },
                            { value: 'dark', label: t('settings.dark'), icon: '🌙' },
                        ].map(th => (
                            <div
                                key={th.value}
                                onClick={() => handleThemeSelect(th.value)}
                                style={{
                                    flex: 1,
                                    padding: '1rem',
                                    border: `2px solid ${theme === th.value ? '#3b82f6' : '#e8ecf0'}`,
                                    borderRadius: '10px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    background: theme === th.value ? '#eff6ff' : 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    transition: 'all 0.2s',
                                    opacity: saving ? 0.7 : 1,
                                }}
                            >
                                <span style={{ fontSize: '1.5rem' }}>{th.icon}</span>
                                <span style={{
                                    fontWeight: theme === th.value ? '700' : '500',
                                    color: theme === th.value ? '#1d4ed8' : '#1e293b',
                                }}>
                                    {th.label}
                                </span>
                                {theme === th.value && (
                                    <span style={{
                                        marginLeft: 'auto',
                                        background: '#3b82f6',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                    }}>✓</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Save settings button */}
                {success && <div className="success-message">{success}</div>}
                {error && <div className="error-message">{error}</div>}

                {/* Change password */}
                <div className="card">
                    <h2>{t('settings.change_password')}</h2>

                    {passwordSuccess && (
                        <div className="success-message">{passwordSuccess}</div>
                    )}
                    {passwordError && (
                        <div className="error-message">{passwordError}</div>
                    )}

                    <div className="form-group">
                        <label>{t('settings.current_password')}</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </div>
                    <div className="form-group">
                        <label>{t('settings.new_password')}</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </div>
                    <div className="form-group">
                        <label>{t('settings.confirm_password')}</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </div>

                    <button
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                        className="btn-primary"
                    >
                        {changingPassword ? t('settings.saving') : t('settings.save_password')}
                    </button>
                </div>
            </div>
        </Layout>
    );
}

export default SettingsPage;